//@ts-ignore
import { tw } from "@careinfo/tailwind";
import { useCallback, useState, useEffect, useRef } from "react";
import { set, useForm } from "react-hook-form";
import usePostHook from "../../hooks/usePostHook";
import usePostFormDataHook from "../../hooks/usePostFormDataHook";
import APIS from "../../constant/Endpoint";
import { toast } from "react-toastify";
import { useEditor, EditorContent, Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Image from "@tiptap/extension-image";
import { useLucideIcon } from "../../Icons/lucide-react/index";

import {
  EmailFormValues,
  AttachedFile,
  PreviewFile,
  UploadedFileMetadata,
} from "./types";
import { EMAIL_REGEX } from "../shared/constants";
import { generateFileId } from "../shared/helpers";
import { EmailChip } from "./EmailChip";
import { AttachmentCard } from "../shared/AttachmentCard";
import { EditorButton } from "../shared/EditorButton";
import { PreviewModal } from "../shared/PreviewModal";
import useGetHook from "../../hooks/useUserServiceGetHook";
import EmailSuggestions from "../shared/EmailSuggestions";


const EmailForm = ({ closeNewEmail, isMaximized, setMailData }) => {
  // State
  const [showCC, setShowCC] = useState<boolean>(false);
  const [showBCC, setShowBCC] = useState<boolean>(false);
  const [toEmails, setToEmails] = useState<string[]>([]);
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [bccEmails, setBccEmails] = useState<string[]>([]);
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [previewFile, setPreviewFile] = useState<PreviewFile | null>(null);
  
  // Autocomplete state
  const [toSuggestions, setToSuggestions] = useState([]);
  const [ccSuggestions, setCcSuggestions] = useState([]);
  const [bccSuggestions, setBccSuggestions] = useState([]);
  const [toSelectedIndex, setToSelectedIndex] = useState(-1);
  const [ccSelectedIndex, setCcSelectedIndex] = useState(-1);
  const [bccSelectedIndex, setBccSelectedIndex] = useState(-1);
  
  // Refs for input fields
  const toInputRef = useRef(null);
  const ccInputRef = useRef(null);
  const bccInputRef = useRef(null);

  const { List, Paperclip, ListOrdered, TextBold, TextItalic, TextUnderline } =
    useLucideIcon();

  // Form hook
  const {
    register,
    handleSubmit,
    setError,
    setValue,
    watch,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm<EmailFormValues>();

  // API hooks
  const { mutateAsync, isPostLoading } = usePostHook({
    queryKey: ["send-email"],
  });

  const { mutateAsync: uploadFileMutation } = usePostFormDataHook({
    queryKey: ["upload-file"],
  });

  const { data: signature, isLoading } = useGetHook({
    queryKey: [`signature`],
    url: `${APIS.EMAIL}signature/`,
    params: {},
  });

  const { data: contact, isLoading: contactLoading } = useGetHook({
    queryKey: [`contact`],
    url: `${APIS.CONTACTS}`,
    params: {},
  });
console.log("contact:",contact);

  // Filter contacts based on input
  const filterContacts = useCallback(
    (inputValue: string) => {
      if (!inputValue || !contact) return [];
      
      const searchTerm = inputValue.toLowerCase();
      return contact.filter((c) => {
        const email = c.email?.toLowerCase() || "";
        const name = c.name?.toLowerCase() || "";
        return email.includes(searchTerm) || name.includes(searchTerm);
      }).slice(0, 5); // Limit to 5 suggestions
    },
    [contact]
  );

  // Watch input changes for autocomplete
  const toInput = watch("to");
  const ccInput = watch("cc");
  const bccInput = watch("bcc");
  const subjectInput = watch("subject");
  

   useEffect(() => {
    if (subjectInput) {
      setMailData((prev) => ({ ...prev, subject: subjectInput }) );
    } 
  }, [subjectInput]);

  useEffect(() => {
    if (toInput) {
      setToSuggestions(filterContacts(toInput));
      setToSelectedIndex(-1);
    } else {
      setToSuggestions([]);
    }
  }, [toInput, filterContacts]);

  useEffect(() => {
    if (ccInput) {
      setCcSuggestions(filterContacts(ccInput));
      setCcSelectedIndex(-1);
    } else {
      setCcSuggestions([]);
    }
  }, [ccInput, filterContacts]);

  useEffect(() => {
    if (bccInput) {
      setBccSuggestions(filterContacts(bccInput));
      setBccSelectedIndex(-1);
    } else {
      setBccSuggestions([]);
    }
  }, [bccInput, filterContacts]);

  // Editor configuration
  const editor: Editor | null = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Image.configure({
        inline: true,
        allowBase64: true,
        resize: {
          enabled: true,
          alwaysPreserveAspectRatio: true,
        },
      }),
    ],
    editorProps: {
      attributes: {
        class: "pt-2 min-h-[300px] focus:outline-none prose max-w-none",
      },
    },
    shouldRerenderOnTransaction: true,
    content: "",
  });

  useEffect(() => {
    if (editor && signature && !isLoading) {      
      const sigContent = signature?.signature || "";
      const initialContent = `<p></p>${sigContent}`;
      if (editor.getHTML() === "<p></p>") {
        editor.commands.setContent(initialContent);
        editor.commands.focus("start");
      }
    }
  }, [editor, signature, isLoading]);

  // File upload helper
  const uploadFile = useCallback(
    async (file: File): Promise<UploadedFileMetadata | null> => {
      const formData = new FormData();
      formData.append("file", file);

      try {
        const uploadUrl = `${APIS.EMAIL}file-upload/`;
        const response = await uploadFileMutation({
          url: uploadUrl,
          formData,
          setError,
        });

        if (response?.status === 201) {
          const payload = response.data;
          return {
            blobId: payload.blobId,
            name: payload.name || file.name,
            size: payload.size || file.size,
            type: payload.type || file.type,
          };
        }

        console.error("File upload failed:", response);
        return null;
      } catch (error: any) {
        console.error("File upload error:", error);
        return null;
      }
    },
    [uploadFileMutation, setError]
  );

  // Email management callbacks
  const addEmail = useCallback(
    (
      targetedInput: keyof EmailFormValues,
      setEmail: React.Dispatch<React.SetStateAction<string[]>>,
      currentEmails: string[]
    ): void => {
      const email = watch(targetedInput)?.trim();
      if (email && EMAIL_REGEX.test(email) && !currentEmails.includes(email)) {
        setEmail((prev) => [...prev, email]);
        setMailData((prev) => ({ ...prev, [targetedInput]: [...currentEmails, email] }));
        setValue(targetedInput, "");

        if (targetedInput === "to") {
          clearErrors("to");
        }
      }
    },
    [watch, setValue, clearErrors]
  );

  const removeEmail = useCallback(
    (
      email: string,
      setEmail: React.Dispatch<React.SetStateAction<string[]>>
    ): void => {
      setEmail((prev) => prev.filter((e) => e !== email));
    },
    []
  );

  // Handle suggestion selection
  const handleSuggestionSelect = useCallback(
    (
      email: string,
      targetedInput: keyof EmailFormValues,
      setEmail: React.Dispatch<React.SetStateAction<string[]>>,
      currentEmails: string[],
      setSuggestions: React.Dispatch<React.SetStateAction<any[]>>
    ) => {
      if (email && !currentEmails.includes(email)) {
        setEmail((prev) => [...prev, email]);
        setMailData((prev) => ({ ...prev, [targetedInput]: [...currentEmails, email] }));
        setValue(targetedInput, "");
        setSuggestions([]);
        
        if (targetedInput === "to") {
          clearErrors("to");
        }
      }
    },
    [setValue, clearErrors]
  );

  const handleKeyDown = useCallback(
    (
      e: React.KeyboardEvent<HTMLInputElement>,
      setEmail: React.Dispatch<React.SetStateAction<string[]>>,
      targetedInput: keyof EmailFormValues,
      currentEmails: string[],
      suggestions: any[],
      selectedIndex: number,
      setSelectedIndex: React.Dispatch<React.SetStateAction<number>>,
      setSuggestions: React.Dispatch<React.SetStateAction<any[]>>
    ): void => {
      // Handle arrow key navigation
      if (suggestions.length > 0) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setSelectedIndex((prev) => 
            prev < suggestions.length - 1 ? prev + 1 : prev
          );
          return;
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          return;
        } else if (e.key === "Enter" && selectedIndex >= 0) {
          e.preventDefault();
          handleSuggestionSelect(
            suggestions[selectedIndex].email,
            targetedInput,
            setEmail,
            currentEmails,
            setSuggestions
          );
          return;
        } else if (e.key === "Escape") {
          setSuggestions([]);
          setSelectedIndex(-1);
          return;
        }
      }

      // Original key handling
      if (["Enter", " "].includes(e.key)) {
        e.preventDefault();
        addEmail(targetedInput, setEmail, currentEmails);
        setSuggestions([]);
      } else if (
        e.key === "Backspace" &&
        watch(targetedInput) === "" &&
        currentEmails.length > 0
      ) {
        setEmail((prev) => prev.slice(0, -1));
      }
    },
    [addEmail, watch, handleSuggestionSelect]
  );

  const handleBlur = useCallback(
    (
      targetedInput: keyof EmailFormValues,
      setEmail: React.Dispatch<React.SetStateAction<string[]>>,
      currentEmails: string[],
      setSuggestions: React.Dispatch<React.SetStateAction<any[]>>
    ): void => {
      // Delay to allow click on suggestion
      setTimeout(() => {
        const val = watch(targetedInput)?.trim();
        if (val) addEmail(targetedInput, setEmail, currentEmails);
        setSuggestions([]);
      }, 200);
    },
    [addEmail, watch]
  );

  // File handling
  const handleFileSubmit = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
      const files = e.target.files;
      if (files && files.length > 0) {
        const newFiles: AttachedFile[] = Array.from(files).map((file) => ({
          file,
          id: generateFileId(file.name),
          uploading: true,
        }));

        setAttachedFiles((prev) => [...prev, ...newFiles]);
        e.target.value = "";

        for (let i = 0; i < newFiles.length; i++) {
          const uploadedMetadata = await uploadFile(newFiles[i].file);

          setAttachedFiles((prev) =>
            prev.map((af) =>
              af.id === newFiles[i].id
                ? {
                    ...af,
                    uploading: false,
                    blobId: uploadedMetadata?.blobId,
                    uploadError: uploadedMetadata ? undefined : "Upload failed",
                  }
                : af
            )
          );
        }
      }
    },
    [uploadFile]
  );

  const removeAttachment = useCallback((id: string): void => {
    setAttachedFiles((prev) => prev.filter((af) => af.id !== id));
  }, []);

  // Memoized remove handlers
  const removeToEmail = useCallback(
    (email: string) => removeEmail(email, setToEmails),
    [removeEmail]
  );
  const removeCcEmail = useCallback(
    (email: string) => removeEmail(email, setCcEmails),
    [removeEmail]
  );
  const removeBccEmail = useCallback(
    (email: string) => removeEmail(email, setBccEmails),
    [removeEmail]
  );

  // Form submission
  const onSubmit = useCallback(
    async (data: EmailFormValues): Promise<void> => {
      if (editor.getHTML() === "<p></p>" && !attachedFiles.length) {
        toast.error("Body is required");
        return;
      }
      if (toEmails.length === 0) {
        setError("to", {
          type: "manual",
          message: "At least one recipient email is required",
        });
        return;
      }

      const stillUploading = attachedFiles.some((af) => af.uploading);
      if (stillUploading) {
        setError("body", {
          type: "manual",
          message: "Please wait for all files to finish uploading",
        });
        return;
      }

      const failedUploads = attachedFiles.filter(
        (af) => af.uploadError || !af.blobId
      );
      if (failedUploads.length > 0) {
        setError("body", {
          type: "manual",
          message:
            "Some files failed to upload. Please remove them and try again.",
        });
        return;
      }

      const url = `${APIS.EMAIL}`;
      const formData = {
        to: toEmails,
        cc: ccEmails.length > 0 ? ccEmails : [],
        bcc: bccEmails.length > 0 ? bccEmails : [],
        subject: data.subject,
        html_body:
          attachedFiles.length || editor.getHTML() !== "<p></p>"
            ? editor.getHTML()
            : null,
        attachments: attachedFiles.map((af) => ({
          blobId: af.blobId,
          name: af.file.name,
          size: af.file.size,
          type: af.file.type,
        })),
      };

      try {
        const res = await mutateAsync({ url, formData, setError });
        if (res?.status === 200) {
          reset();
          setToEmails([]);
          setCcEmails([]);
          setBccEmails([]);
          setAttachedFiles([]);
          setShowCC(false);
          setShowBCC(false);
          editor?.commands.setContent("");
          closeNewEmail();
          setMailData({cc: [], bcc: [], to: [], subject: ''});
        }
      } catch (err) {
        console.error("Email send error:", err);
      }
    },
    [
      toEmails,
      ccEmails,
      bccEmails,
      attachedFiles,
      setError,
      mutateAsync,
      reset,
      editor,
    ]
  );

  const handlePreviewLocal = (attachedFile: AttachedFile) => {
    try {
      const url = URL.createObjectURL(attachedFile.file);
      setPreviewFile({
        url,
        type: attachedFile.file.type,
        name: attachedFile.file.name,
      });
    } catch (err) {
      console.error("Local preview error:", err);
      toast.error("Failed to generate preview");
    }
  };

  if (!editor) {
    return (
      <div className={tw`flex items-center justify-center p-8`}>
        <p className={tw`text-gray-500`}>Loading editor...</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={tw`flex flex-col w-full rounded-md max-h-[80vh] overflow-y-scroll`}
    >
      {/* TO FIELD */}
      <div className={tw`border-b px-2 py-2 flex items-center relative`}>
        <PreviewModal
          isOpen={!!previewFile}
          onClose={() => setPreviewFile(null)}
          url={previewFile?.url || ""}
          name={previewFile?.name || ""}
          type={previewFile?.type || ""}
          file={attachedFiles.find(f => f.file.name === previewFile?.name)?.file}
        />
        <label htmlFor="to-input" className={tw`text-sm text-gray-600 w-8`}>
          To
        </label>
        <div className={tw`flex-1 flex flex-wrap items-center relative`}>
          {toEmails.map((email) => (
            <EmailChip key={email} email={email} onRemove={removeToEmail} />
          ))}
          <input
            ref={toInputRef}
            id="to-input"
            type="text"
            onKeyDown={(e) =>
              handleKeyDown(
                e,
                setToEmails,
                "to",
                toEmails,
                toSuggestions,
                toSelectedIndex,
                setToSelectedIndex,
                setToSuggestions
              )
            }
            placeholder={toEmails.length === 0 ? "Recipients" : ""}
            className={tw`flex-1 text-sm text-gray-800 focus:outline-none min-w-[120px]`}
            {...register("to", {
              onBlur: () => handleBlur("to", setToEmails, toEmails, setToSuggestions),
            })}
            autoComplete="off"
          />
          <EmailSuggestions
            suggestions={toSuggestions}
            onSelect={(email) =>
              handleSuggestionSelect(email, "to", setToEmails, toEmails, setToSuggestions)
            }
            selectedIndex={toSelectedIndex}
            inputRef={toInputRef}
          />
        </div>

        <button
          type="button"
          className={tw`text-xs text-gray-600 hover:text-gray-800 ml-3 focus:outline-none hover:underline ${
            showCC && "underline"
          }`}
          onClick={() => setShowCC((prev) => !prev)}
          aria-expanded={showCC}
        >
          Cc
        </button>

        <button
          type="button"
          className={tw`text-xs text-gray-600 hover:text-gray-800 ml-2 focus:outline-none hover:underline ${
            showBCC && "underline"
          }`}
          onClick={() => setShowBCC((prev) => !prev)}
          aria-expanded={showBCC}
        >
          Bcc
        </button>
      </div>

      {errors.to && (
        <p className={tw`text-red-500 text-xs px-4 mt-1`} role="alert">
          {errors.to.message}
        </p>
      )}

      {/* CC FIELD */}
      {showCC && (
        <div className={tw`border-b px-2 py-2 flex items-center relative`}>
          <label htmlFor="cc-input" className={tw`text-sm text-gray-600 w-8`}>
            Cc
          </label>
          <div className={tw`flex-1 flex flex-wrap items-center relative`}>
            {ccEmails.map((email) => (
              <EmailChip key={email} email={email} onRemove={removeCcEmail} />
            ))}
            <input
              ref={ccInputRef}
              id="cc-input"
              type="text"
              onKeyDown={(e) =>
                handleKeyDown(
                  e,
                  setCcEmails,
                  "cc",
                  ccEmails,
                  ccSuggestions,
                  ccSelectedIndex,
                  setCcSelectedIndex,
                  setCcSuggestions
                )
              }
              placeholder={ccEmails.length === 0 ? "Cc" : ""}
              className={tw`flex-1 text-sm text-gray-800 focus:outline-none min-w-[120px]`}
              {...register("cc", {
                onBlur: () => handleBlur("cc", setCcEmails, ccEmails, setCcSuggestions),
              })}
              autoComplete="off"
            />
            <EmailSuggestions
              suggestions={ccSuggestions}
              onSelect={(email) =>
                handleSuggestionSelect(email, "cc", setCcEmails, ccEmails, setCcSuggestions)
              }
              selectedIndex={ccSelectedIndex}
              inputRef={ccInputRef}
            />
          </div>
        </div>
      )}

      {/* BCC FIELD */}
      {showBCC && (
        <div className={tw`border-b px-2 py-2 flex items-center relative`}>
          <label htmlFor="bcc-input" className={tw`text-sm text-gray-600 w-8`}>
            Bcc
          </label>
          <div className={tw`flex-1 flex flex-wrap items-center relative`}>
            {bccEmails.map((email) => (
              <EmailChip key={email} email={email} onRemove={removeBccEmail} />
            ))}
            <input
              ref={bccInputRef}
              id="bcc-input"
              type="text"
              onKeyDown={(e) =>
                handleKeyDown(
                  e,
                  setBccEmails,
                  "bcc",
                  bccEmails,
                  bccSuggestions,
                  bccSelectedIndex,
                  setBccSelectedIndex,
                  setBccSuggestions
                )
              }
              placeholder={bccEmails.length === 0 ? "Bcc" : ""}
              className={tw`flex-1 text-sm text-gray-800 focus:outline-none min-w-[120px]`}
              {...register("bcc", {
                onBlur: () => handleBlur("bcc", setBccEmails, bccEmails, setBccSuggestions),
              })}
              autoComplete="off"
            />
            <EmailSuggestions
              suggestions={bccSuggestions}
              onSelect={(email) =>
                handleSuggestionSelect(email, "bcc", setBccEmails, bccEmails, setBccSuggestions)
              }
              selectedIndex={bccSelectedIndex}
              inputRef={bccInputRef}
            />
          </div>
        </div>
      )}

      {/* SUBJECT */}
      <div className={tw`border-b px-2 py-2 flex items-center`}>
        <label
          htmlFor="subject-input"
          className={tw`text-sm text-gray-600 w-14`}
        >
          Subject
        </label>
        <input
          id="subject-input"
          type="text"
          placeholder="Subject"
          className={tw`flex-1 text-sm text-gray-800 focus:outline-none`}
          {...register("subject", {
            required: "Subject is required",
          })}
        />
      </div>
      {errors.subject && (
        <p className={tw`text-red-500 text-xs px-4 mt-1`} role="alert">
          {errors.subject.message}
        </p>
      )}

      {/* BODY */}
      <EditorContent editor={editor} />

      {errors.body && (
        <p className={tw`text-red-500 text-xs px-4 mt-1`} role="alert">
          {errors.body.message}
        </p>
      )}

      {/* ATTACHMENTS DISPLAY */}
      {attachedFiles.length > 0 && (
        <div className={tw`px-4 py-3 border-t bg-gray-50`}>
          <div className={tw`flex flex-wrap gap-2`}>
            {attachedFiles.map((file) => (
              <AttachmentCard
                key={file.id}
                file={file}
                onRemove={removeAttachment}
                onPreview={handlePreviewLocal}
              />
            ))}
          </div>
        </div>
      )}

      {/* SEND BUTTON */}
      <div
        className={tw` flex flex-col justify-end ${
          isMaximized && "h-[45vh]"
        }  `}
      >
        <div
          className={tw`px-2 py-3 border-t  flex justify-between items-end flex-wrap gap-2 `}
        >
          <button
            type="submit"
            disabled={isPostLoading}
            className={tw`bg-primary text-white px-5 py-2 rounded-lg shadow hover:bg-orange-500 transition font-medium text-sm ${
              isPostLoading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isPostLoading ? "Sending..." : "Send"}
          </button>

          <div className={tw`flex gap-2 flex-wrap`}>
            <label
              title="Attach files"
              className={tw`flex items-center cursor-pointer rounded-md border transition font-medium hover:text-red-400 p-2`}
            >
              <Paperclip size={20} />
              <input
                type="file"
                multiple
                className="hidden"
                onChange={handleFileSubmit}
                aria-label="Attach files"
              />
            </label>

            <EditorButton
              title="Bold (Ctrl+B)"
              isActive={editor.isActive("bold")}
              onClick={() => editor.chain().focus().toggleBold().run()}
              icon={<TextBold />}
            />
            <EditorButton
              title="Italic (Ctrl+I)"
              isActive={editor.isActive("italic")}
              onClick={() => editor.chain().focus().toggleItalic().run()}
              icon={<TextItalic />}
            />
            <EditorButton
              title="Underline (Ctrl+U)"
              isActive={editor.isActive("underline")}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              icon={<TextUnderline />}
            />
            <EditorButton
              title="Numbered list (Ctrl+Shift+7)"
              isActive={editor.isActive("orderedList")}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              icon={<ListOrdered />}
            />
            <EditorButton
              title="Bulleted list (Ctrl+Shift+8)"
              isActive={editor.isActive("bulletList")}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              icon={<List />}
            />
          </div>
        </div>
      </div>
    </form>
  );
};

export default EmailForm;